import { useEffect, useState } from 'react';

// 새 판 알림(2026-10-19 오너) — "배포하면 F5 를 안 누른 사용자한테 패치노트 버튼이 깜빡여서 F5 를
// 누르게". 열려 있는 콘솔은 처음 받은 번들로 계속 돈다. 그래서 몇 분마다(그리고 창으로 돌아올 때)
// index.html 을 새로 받아 **진입 번들 이름**(assets/index-XXXX.js)이 지금 도는 것과 다른지 본다.
// index.html 은 no-cache 로 올라가고 배포 때 CDN 도 비우므로, 다르면 곧 새 판이 나간 것이다.
// 강제로 새로고침하지는 않는다 — 쓰던 내용이 날아갈 수 있다. 버튼을 보여 주고 누르게 한다.

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/** index.html 본문에서 진입 번들 경로(첫 module 스크립트의 src). 못 찾으면 null. */
export function entryScriptOf(html: string): string | null {
  for (const tag of html.match(/<script\b[^>]*>/gi) ?? []) {
    if (!/type\s*=\s*["']module["']/i.test(tag)) continue;
    const src = /src\s*=\s*["']([^"']+)["']/i.exec(tag)?.[1];
    if (src) return pathOf(src);
  }
  return null;
}

/** 지금 이 화면을 띄운 진입 번들 경로. */
export function currentEntryScript(doc: Document = document): string | null {
  const el = doc.querySelector<HTMLScriptElement>('script[type="module"][src]');
  return el ? pathOf(el.getAttribute('src') ?? '') : null;
}

function pathOf(src: string): string {
  try {
    return new URL(src, 'https://x.invalid').pathname;
  } catch {
    return src;
  }
}

/** 서버의 index.html 이 가리키는 진입 번들. 네트워크가 안 되면 null(알림 없음). */
export async function fetchLatestEntryScript(): Promise<string | null> {
  try {
    const res = await fetch(`/index.html?v=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    return entryScriptOf(await res.text());
  } catch {
    return null;
  }
}

/**
 * 새 판이 배포됐으면 true. 한 번 true 가 되면 더 묻지 않는다.
 * 개발 서버(vite)에서는 끈다 — 진입이 /src/main.tsx 라 판을 가를 수 없다.
 */
export function useNewVersionAvailable(
  opts: {
    enabled?: boolean;
    current?: string | null;
    intervalMs?: number;
    fetchLatest?: () => Promise<string | null>;
  } = {},
): boolean {
  const enabled = opts.enabled ?? import.meta.env.PROD;
  const intervalMs = opts.intervalMs ?? CHECK_INTERVAL_MS;
  const fetchLatest = opts.fetchLatest ?? fetchLatestEntryScript;
  const [current] = useState(() =>
    opts.current !== undefined ? opts.current : currentEntryScript(),
  );
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled || !current || ready) return;
    let alive = true;
    const check = async () => {
      const latest = await fetchLatest();
      if (alive && latest && latest !== current) setReady(true);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    const timer = window.setInterval(() => void check(), intervalMs);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      alive = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [enabled, current, ready, intervalMs, fetchLatest]);

  return ready;
}
